import PlatformBounds from "@mapmycustomers/shared/types/map/PlatformBounds";
import PlatformFilterModel, {
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import SkippedRecordsCardConfiguration, {
  SkippedRecordsCriteria,
} from "@app/types/dashboard/cards/skippedRecords/SkippedRecordsCardConfiguration";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { createEmptyFilterModel } from "@app/util/filters/utils";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

interface DateRange {
  endDate: Date;
  startDate: Date;
}

/**
 * The dotted-field conditions defining a "skipped record" for the day, shared by the pins query
 * and the View Data drill-down so both surfaces stay consistent.
 *
 * When Activity Type(s) are selected we filter on `activityTypeIds $nin [...]` — "did not log
 * those activities", which intentionally includes records that logged *other* activity types and
 * drops the `visitStatus = skipped` condition. With none selected we fall back to
 * `visitStatus = skipped` (no activity logged at all).
 */
export const buildSkippedRecordsConditions = (
  criteria: Pick<SkippedRecordsCriteria, "activityTypeIds" | "sources">,
  range: DateRange
): PlatformFilterCondition[] => {
  const { activityTypeIds, sources } = criteria;

  return [
    {
      "tripInteractions.trip.startTime": {
        $gte: range.startDate.toISOString(),
        $lte: range.endDate.toISOString(),
      },
    },
    activityTypeIds?.length
      ? { "tripInteractions.activityTypeIds": { $nin: activityTypeIds } }
      : { "tripInteractions.visitStatus": { $in: ["skipped"] } },
    ...(sources?.length ? [{ "sourceCreated.source": { $in: sources } }] : []),
  ];
};

/**
 * Builds the `$filters` object for the multi-entity `/pins` endpoint, scoped to SKIPPED records
 * for the selected entity type in the Skipped Records Mini-Map widget.
 *
 * Shape mirrors what `onFetchPins` in the map saga produces:
 *   - `bounds` at the top level
 *   - `entities.<entityType>` containing the skipped conditions plus the user's universal filters
 *
 * Note: dashboard scope (team/user) is applied at the top level by the saga, not here.
 */
const buildSkippedRecordsPinsFilters = (
  configuration: SkippedRecordsCardConfiguration,
  range: DateRange,
  bounds: PlatformBounds | undefined
): PlatformFilterModel => {
  const { entityType, filters } = configuration.criteria;

  const fieldModel = getFieldModelByEntityType(entityType);
  // A saved card may have no universal filters set; convertToPlatformFilterModel reads
  // `.conditions` and throws on undefined, which would abort the whole pins fetch and hang
  // the card on its loading spinner. Fall back to an empty filter model in that case.
  const universalPlatformFilters = convertToPlatformFilterModel(
    filters ?? createEmptyFilterModel(),
    fieldModel.fields.map((field) => ({ field, visible: true })),
    fieldModel
  );

  const entityFilter: PlatformFilterModel = {
    ...universalPlatformFilters,
    $and: [
      ...(universalPlatformFilters.$and ?? []),
      ...buildSkippedRecordsConditions(configuration.criteria, range),
    ],
  };

  return {
    bounds,
    entities: {
      [entityType]: entityFilter,
    },
  };
};

export default buildSkippedRecordsPinsFilters;
